import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Dropdown } from 'primeng/dropdown';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ControlIdentity, CplaceSingleIconControlChange, CplaceSingleIconControlParameters, Icon } from '../../../api/model/models';
import { CplaceControlWithEditModeComponent, CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR } from '../shared';
import { CplaceControlService } from '../shared/cplace-control.service';

@Component({
  selector: 'cf-frontend-sdk-cplace-single-icon-control',
  templateUrl: './cplace-single-icon-control.component.html',
  styleUrls: ['./styles/cplace-single-icon-control.component.scss'],
  providers: [CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR(CplaceSingleIconControlComponent)],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CplaceSingleIconControlComponent extends CplaceControlWithEditModeComponent<Icon | undefined> {
  public static readonly componentName = 'CplaceSingleIconControlComponent';

  @ViewChild('dropdown')
  public dropdown!: Dropdown;

  /**
   * Available icons that the user may select from.
   */
  @Input() public availableIcons: Icon[] = [];

  @HostBinding('class.cf-frontend-sdk-cplace-single-icon-control') public cplaceSingleIconControl = true;

  /**
   * Whether the label of the icons should be displayed in the dropdown.
   */
  @Input() public displayIconLabels = true;

  /**
   * Whether the empty value view should be shown.
   */
  public $showEmptyValue: Observable<boolean> = combineLatest([this.$value, this.$editMode]).pipe(
    map(([value, editMode]) => {
      return !value && !editMode;
    })
  );

  /**
   * Whether the clear button should be shown.
   */
  public $showClear: Observable<boolean> = combineLatest([this.$value, this.$editMode]).pipe(
    map(([value, editMode]) => {
      return !!value && editMode;
    })
  );

  constructor(
    public elementRef: ElementRef<HTMLElement>,
    public changeDetectorRef: ChangeDetectorRef,
    public cplaceControlService: CplaceControlService
  ) {
    super(elementRef, changeDetectorRef, cplaceControlService);
  }

  public onNgModelChange() {
    this.dropdown.focus();
  }

  protected initializeControlParameters(controlParameters: CplaceSingleIconControlParameters): void {
    this.value = controlParameters.value;
    this.availableIcons = controlParameters.availableIcons;
  }

  protected getControlChange(identity: ControlIdentity, value: Icon | undefined): CplaceSingleIconControlChange {
    return {
      featureName: identity.featureName,
      componentName: CplaceSingleIconControlComponent.componentName,
      value: value?.name,
    };
  }

  protected getComponentClassName(): string {
    return CplaceSingleIconControlComponent.componentName;
  }

  protected onClear(): void {
    this.value = undefined;
  }

  protected afterSubmitHook() {
    super.afterSubmitHook();
    this.dropdown.focused = false;
  }
}
